/**
 * Service worker of a production build (src/pwa/service-worker.js, built by
 * scripts/vite-plugin-sw-precache.ts). In development any worker left over is
 * removed, so HMR never goes through a cache.
 */

/** Fired on window when a new build took over a page that already had one. */
export const APP_UPDATED_EVENT = 'yodi:app-updated';

const UPDATE_CHECK_MS = 60 * 60_000;
const RELOAD_GUARD_KEY = 'yodi-preload-reload-at';
const RELOAD_GUARD_MS = 30_000;

function watchForUpdates(registration: ServiceWorkerRegistration) {
  // A long-lived tab (the installed app) checks for a new build now and then.
  window.setInterval(() => {
    if (navigator.onLine && document.visibilityState === 'visible') {
      registration.update().catch(() => undefined);
    }
  }, UPDATE_CHECK_MS);
}

/**
 * A lazy chunk of a build that is no longer served (a deploy happened while the
 * tab was open, and it is not in the cache either): reload once onto the
 * current build instead of leaving a blank screen.
 */
function reloadOnStaleChunk() {
  window.addEventListener('vite:preloadError', (event) => {
    if (!navigator.onLine) return;
    try {
      const last = Number(sessionStorage.getItem(RELOAD_GUARD_KEY) ?? 0);
      if (Date.now() - last < RELOAD_GUARD_MS) return;
      sessionStorage.setItem(RELOAD_GUARD_KEY, String(Date.now()));
    } catch {
      return;
    }
    event.preventDefault();
    window.location.reload();
  });
}

export function registerServiceWorker(): void {
  if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return;

  if (!import.meta.env.PROD) {
    navigator.serviceWorker
      .getRegistrations()
      .then((registrations) => registrations.forEach((registration) => void registration.unregister()))
      .catch(() => undefined);
    return;
  }

  reloadOnStaleChunk();

  // The first install takes control without a previous build: nothing to announce.
  let hadController = Boolean(navigator.serviceWorker.controller);
  navigator.serviceWorker.addEventListener('controllerchange', () => {
    if (hadController) window.dispatchEvent(new CustomEvent(APP_UPDATED_EVENT));
    hadController = true;
  });

  const register = () =>
    navigator.serviceWorker
      // The worker script itself always comes from the server, never the HTTP cache.
      .register('/service-worker.js', { updateViaCache: 'none' })
      .then(watchForUpdates)
      .catch((error: unknown) => console.warn('Service worker registration failed', error));

  // After the page has loaded, so precaching never competes with the first paint.
  if (document.readyState === 'complete') void register();
  else window.addEventListener('load', () => void register(), { once: true });
}
